import type { ApiClientV2 } from 'utils/api-client/api-client.utils'

import { FromSwagger } from 'types/helpers'
import { ShopPaymentTicketAttributes } from 'types/__generated__/api'

export type AssignedSeat = FromSwagger<ShopPaymentTicketAttributes>

export interface AttendeeAssignment {
  ticketId: number
  emails: string[]
}

// Explicit per-seat entry: the backend assigns exactly this seat, nothing positional.
export interface SeatAssignment {
  seatId: number
  email: string
}

export interface AssignCountMismatch {
  type: 'count_mismatch' | 'edit_not_allowed'
  ticketId: number
  seats: number
  emails: number
}

export interface AssignError {
  code?: string
  status?: string
  title?: string
  detail?: string
  meta?: AssignCountMismatch | Record<string, unknown>
}

// ApiClientV2 has no `responseType`; presence of `errors` is the discriminator.
export type AssignResponse = { data: AssignedSeat[]; status?: number } | { errors: AssignError[]; status?: number }

export const assignFailed = (resp: AssignResponse | undefined): resp is { errors: AssignError[]; status?: number } =>
  Array.isArray((resp as { errors?: unknown } | undefined)?.errors) &&
  (resp as { errors: AssignError[] }).errors.length > 0

// 404 on this surface means the switch is off (or the order vanished). Status is on the envelope or the first error.
export const assignmentsClosed = (resp: AssignResponse | undefined): boolean => {
  if (!assignFailed(resp)) return false

  return Number(resp.status ?? resp.errors[0]?.status) === 404
}

const BASE_URL = (username: string, token: string) => `shop/${username}/orders/${token}/attendee_assignments`

// Client is read at call time: a module-level store has no root, so destructuring POST at import throws.
// Recaptcha goes in `meta`, never attributes (backend reads meta only).
export const createAttendeeAssignmentsApi = (client?: ApiClientV2) => ({
  assign: (
    username: string,
    token: string,
    data: { assignments: Array<AttendeeAssignment | SeatAssignment>; notify?: boolean; recaptcha?: string }
  ): Promise<AssignResponse> => {
    if (!client) {
      return Promise.resolve({ errors: [{ title: 'No API client. This store was built without a root store.' }] })
    }

    const { recaptcha, ...attributes } = data

    return client.POST<AssignedSeat[]>(BASE_URL(username, token), {
      ...attributes,
      ...(recaptcha ? { meta: { recaptcha } } : {}),
    }) as unknown as Promise<AssignResponse>
  },

  // POST not DELETE: captcha must not go in a query string, and one request is one transaction.
  removeAssignments: (
    username: string,
    token: string,
    data: { seatIds: number[]; recaptcha?: string }
  ): Promise<AssignResponse> => {
    if (!client) {
      return Promise.resolve({ errors: [{ title: 'No API client. This store was built without a root store.' }] })
    }

    const { recaptcha, ...attributes } = data

    return client.POST<AssignedSeat[]>(`${BASE_URL(username, token)}/remove`, {
      ...attributes,
      ...(recaptcha ? { meta: { recaptcha } } : {}),
    }) as unknown as Promise<AssignResponse>
  },
})

export type AttendeeAssignmentsApi = ReturnType<typeof createAttendeeAssignmentsApi>
